import { Check, Palette, RestartAlt } from "@mui/icons-material";
import { Box, IconButton, Stack, TextField, Tooltip, Typography } from "@mui/material";
import isEqual from "lodash/isEqual";
import { type FC, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { SaveAsDefaultDialog } from "../../components/SaveAsDefault/SaveAsDefaultDialog";
import { CategoricalColorPinsSection, type ColorPinEntry } from "../../components/UIToolkit/Inputs/CategoricalColorPinsSection";
import { SystemDropdown } from "../../components/UIToolkit/Inputs/SystemDropDown";
import { SystemValidationTextField } from "../../components/UIToolkit/Inputs/SystemValidationTextField";
import { EmptyState } from "../../components/UIToolkit/molecules/EmptyState";
import SystemDialog from "../../components/UIToolkit/Popups/Dialog";
import SystemButton from "../../components/UIToolkit/SystemButton/SystemButton";
import { useAuth } from "../../contexts/AuthProvider";
import { useAnalytics } from "../../contexts/analytics/hooks";
import { FEATURE_FLAGS } from "../../contexts/FeatureFlagsProvider";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useSnackbar } from "../../contexts/SnackbarContext";
import { useJobColorResolver } from "../../hooks/useJobColors";
import { useAllJobs, useJobs } from "../../hooks/useJobs";
import { useFeatureFlag } from "../../hooks/useOpenFeatureFlag";
import { useRoleColorResolver } from "../../hooks/useRoleColors";
import { useSaveAsDefaultEnabled } from "../../hooks/useSaveAsDefaultEnabled";
import { useAccessibleUserSettings, useRestoreUserSettings, useUpdateUserSettings } from "../../hooks/useUserSettings";
import { type ColorRole, isValidHexColor, pickContrastText, THEME_PRESETS } from "../../theme/palette";
import { type FontRole, useThemePreference } from "../../theme/ThemePreferenceProvider";
import { FONT_OPTIONS, type FontId, LOGO_OPTIONS, type LogoId } from "../../theme/tokens";
import { sortJobsByDisplayName } from "../../utils/jobs";
import { NurseFullRole, type UpdateUserSettingsRequest } from "../../utils/models";
import { isHeadNurse } from "../../utils/utilFunctions";

interface AppearanceDialogProps {
	open: boolean;
	onClose: () => void;
}

const SWATCH_SIZE = 40;
const COLOR_ROLES: readonly ColorRole[] = ["primary", "secondary", "tertiary", "neutral"];
const FONT_ROLES: readonly FontRole[] = ["headline", "body", "label"];
const COLOR_NAMES = ["violet", "amber", "blue", "pink", "green", "brown", "cyan", "olive", "plum", "navy", "rose", "teal"] as const;
type PinField = "job_colors" | "role_colors";
type AppearanceContext = { departmentId: string; userId: string; open: boolean };

/**
 * A single color slot: the OS color picker (a native primitive — no UIToolkit
 * equivalent) + the toolkit's `SystemValidationTextField` for the hex + a live
 * swatch. Commits a valid hex live; fires `onCommit` (analytics) on blur.
 */
const ColorRoleField: FC<{
	role: ColorRole;
	label: string;
	value: string;
	invalidHelper: string;
	onChange: (hex: string) => void;
	onCommit: (hex: string) => void;
}> = ({ role, label, value, invalidHelper, onChange, onCommit }) => {
	const [draft, setDraft] = useState<string>(value);

	useEffect(() => {
		setDraft(value);
	}, [value]);

	const draftValid = isValidHexColor(draft);
	const resolved = draftValid ? draft : value;

	const handleDraft = (next: string) => {
		const upper = next.toUpperCase();
		setDraft(upper);
		if (isValidHexColor(upper)) onChange(upper);
	};

	const commit = () => {
		if (draftValid) onCommit(draft);
	};

	return (
		<Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
			<TextField
				type="color"
				size="small"
				value={resolved}
				onChange={(e) => handleDraft(e.target.value)}
				onBlur={commit}
				sx={{ width: 56, "& .MuiOutlinedInput-root": { height: 40 }, "& input": { p: 0.5, height: "100%", boxSizing: "border-box", cursor: "pointer" } }}
				slotProps={{ htmlInput: { "aria-label": `${label} color picker`, "data-testid": `appearance-${role}-color-input` } }}
			/>
			<SystemValidationTextField
				label={label}
				value={draft}
				onChange={(e) => handleDraft(e.target.value)}
				reserveHeightForHelperText={false}
				validation={{
					validationCallback: isValidHexColor,
					performValidation: "onlyAfterInitialChange",
					showValidWhenValid: "never",
					helperText: { invalid: invalidHelper },
				}}
				dataTestId={`appearance-${role}-hex-input`}
				injectedProps={{ onBlur: commit, slotProps: { htmlInput: { maxLength: 7 } } }}
				formControlSx={{ width: 150 }}
			/>
			<Box sx={{ width: 28, height: 28, borderRadius: 1, bgcolor: resolved, border: "1px solid", borderColor: "divider" }} />
		</Stack>
	);
};

/** A single typography slot: the toolkit's `SystemDropdown`, each option previewed in its own face. */
const FontRoleField: FC<{ role: FontRole; label: string; value: FontId; onChange: (id: FontId) => void }> = ({ role, label, value, onChange }) => (
	<SystemDropdown<FontId>
		label={label}
		fullWidth
		data-testid={`appearance-${role}-font-select`}
		selectedItem={value}
		onChange={(id) => {
			if (id) onChange(id);
		}}
		items={FONT_OPTIONS.map((font) => ({
			id: font.id,
			value: font.id,
			label: () => (
				<Box component="span" sx={{ fontFamily: font.stack }}>
					{font.label}
				</Box>
			),
			selectedLabel: () => (
				<Box component="span" sx={{ fontFamily: font.stack }}>
					{font.label}
				</Box>
			),
		}))}
	/>
);

/** Support-only logo selector: a card per bundled logo, showing the header wordmark preview. */
const LogoPicker: FC<{ value: LogoId; onChange: (id: LogoId) => void }> = ({ value, onChange }) => (
	<Stack direction="row" spacing={1.25} useFlexGap sx={{ flexWrap: "wrap" }}>
		{LOGO_OPTIONS.map((logo) => {
			const selected = logo.id === value;
			return (
				<Box
					key={logo.id}
					role="button"
					tabIndex={0}
					aria-label={logo.label}
					aria-pressed={selected}
					data-testid={`appearance-logo-${logo.id}`}
					onClick={() => onChange(logo.id)}
					onKeyDown={(e) => {
						if (e.key === "Enter" || e.key === " ") {
							e.preventDefault();
							onChange(logo.id);
						}
					}}
					sx={{
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						gap: 0.75,
						px: 2,
						py: 1.5,
						minWidth: 120,
						borderRadius: 1.5,
						cursor: "pointer",
						border: selected ? "2px solid" : "1px solid",
						borderColor: selected ? "primary.main" : "divider",
						transition: "border-color 120ms",
					}}
				>
					<Box component="img" src={logo.header} alt={logo.label} sx={{ height: 24, width: "auto", maxWidth: 100, objectFit: "contain" }} />
					<Typography variant="caption" sx={{ color: selected ? "primary.main" : "text.secondary" }}>
						{logo.label}
					</Typography>
				</Box>
			);
		})}
	</Stack>
);

export const AppearanceDialog: FC<AppearanceDialogProps> = ({ open, onClose }) => {
	const { t } = useTranslation();
	const { colors, fonts, logo, setColor, setFont, setLogo, resetAll, isCustom } = useThemePreference();
	const { track, trackError } = useAnalytics();
	const { showSnackbar } = useSnackbar();
	const { userInfo } = useAuth();
	const { chosenDepartment } = useHospitalData();
	const departmentId = chosenDepartment.department_id;
	const userId = userInfo.id;
	const jobs = useJobs();
	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);
	const enabledJobIds = chosenDepartment.jobIds;
	const departmentJobs = chosenDepartment.jobs;
	const localJobsComplete =
		enabledJobIds !== undefined && (enabledJobIds.length === 0 || (departmentJobs !== undefined && enabledJobIds.every((id) => departmentJobs.some((job) => job.id === id))));
	const allJobs = useAllJobs(open && jobManagementEnabled && !localJobsComplete && enabledJobIds !== undefined && enabledJobIds.length > 0);
	const fetchedJobs = allJobs.data;
	const catalogJobsComplete = enabledJobIds !== undefined && fetchedJobs !== undefined && enabledJobIds.every((id) => fetchedJobs.some((job) => job.id === id));
	const jobCatalogReady = !jobManagementEnabled || localJobsComplete || catalogJobsComplete;
	const jobCatalogUnavailable = jobManagementEnabled && !jobCatalogReady && (enabledJobIds === undefined || allJobs.isError || allJobs.isSuccess);
	const saveAsDefaultEnabled = useSaveAsDefaultEnabled() && isHeadNurse(userInfo.role);
	const settings = useAccessibleUserSettings();
	const { mutateAsync: updateSettings, isPending: isSaving } = useUpdateUserSettings();
	const { mutateAsync: restoreSettings, isPending: isRestoring } = useRestoreUserSettings();
	const [jobPins, setJobPins] = useState<Record<string, number>>({});
	const [rolePins, setRolePins] = useState<Record<string, number>>({});
	const [initializedContext, setInitializedContext] = useState<AppearanceContext | null>(null);
	const [confirmDefault, setConfirmDefault] = useState(false);
	const [confirmDiscard, setConfirmDiscard] = useState(false);
	const operationInFlight = useRef<symbol | null>(null);
	const activeContext = useRef<AppearanceContext>({ departmentId, userId, open });
	const jobColor = useJobColorResolver(jobPins);
	const roleColor = useRoleColorResolver(rolePins);
	const isSupport = !!userInfo?.support;
	const initializedForContext = initializedContext?.departmentId === departmentId && initializedContext.userId === userId;
	const isCurrentOperation = (token: symbol): boolean =>
		operationInFlight.current === token &&
		activeContext.current.departmentId === departmentId &&
		activeContext.current.userId === userId &&
		activeContext.current.open;
	const roleEntries: ColorPinEntry[] = chosenDepartment.roles.flatMap((code) => {
		// The cast indexes the known enum; unknown department role codes have no full role to colour.
		const role = NurseFullRole[code as keyof typeof NurseFullRole];
		return role ? [{ id: code, label: role }] : [];
	});
	// DEFAULT: An unresolved catalog has no rows; its section renders only when jobCatalogReady.
	const catalogJobs = localJobsComplete
		? jobs
		: fetchedJobs && enabledJobIds
			? sortJobsByDisplayName(fetchedJobs.filter((job) => enabledJobIds.includes(job.id)))
			: [];
	const jobEntries: ColorPinEntry[] = jobManagementEnabled && jobCatalogReady ? catalogJobs.map((job) => ({ id: job.id, label: job.displayName })) : [];
	const jobChanged = initializedForContext && settings.data !== undefined && !isEqual(jobPins, settings.data.job_colors);
	const roleChanged = initializedForContext && settings.data !== undefined && !isEqual(rolePins, settings.data.role_colors);
	const colorsChanged = jobChanged || roleChanged;
	const defaultNotice =
		jobChanged && roleChanged
			? t("appearance.category.save.default.both.notice")
			: jobChanged
				? t("appearance.category.save.default.jobs.notice")
				: roleChanged
					? t("appearance.category.save.default.roles.notice")
					: undefined;
	const colorNames = COLOR_NAMES.map((name) => t(`settings.job.colors.swatch.${name}`));
	const requestClose = (): void => {
		if (colorsChanged) setConfirmDiscard(true);
		else onClose();
	};

	useLayoutEffect(() => {
		if (activeContext.current.departmentId !== departmentId || activeContext.current.userId !== userId) {
			setConfirmDefault(false);
			setConfirmDiscard(false);
		}
		activeContext.current = { departmentId, userId, open };
		return () => {
			operationInFlight.current = null;
		};
	}, [departmentId, userId, open]);

	useEffect(() => {
		if (!open) {
			setInitializedContext(null);
			setConfirmDefault(false);
			setConfirmDiscard(false);
			return;
		}
		if (settings.data && !initializedForContext) {
			setJobPins(settings.data.job_colors);
			setRolePins(settings.data.role_colors);
			setInitializedContext({ departmentId, userId, open });
		}
	}, [open, settings.data, initializedForContext, departmentId, userId]);

	const saveColors = async (asDefault: boolean): Promise<void> => {
		if (!jobChanged && !roleChanged) return;
		if (operationInFlight.current !== null) return;
		const token = Symbol();
		operationInFlight.current = token;
		const request: UpdateUserSettingsRequest = {};
		if (jobChanged) request.jobColors = jobPins;
		if (roleChanged) request.roleColors = rolePins;
		if (asDefault) request.saveAsDefault = true;
		track("appearance_colors_save_start", { department_id: departmentId, save_as_default: asDefault });
		try {
			await updateSettings(request);
			if (!isCurrentOperation(token)) return;
			track("appearance_colors_save_success", { department_id: departmentId, save_as_default: asDefault });
			setConfirmDefault(false);
			showSnackbar(t("appearance.category.save.success"), "success");
			onClose();
		} catch (error) {
			trackError("appearance_colors_save_error", { error_message: error instanceof Error ? error.message : String(error) });
			if (isCurrentOperation(token)) showSnackbar(t("appearance.category.save.failed"), "error");
		} finally {
			if (operationInFlight.current === token) operationInFlight.current = null;
		}
	};

	const restoreColors = async (field: PinField): Promise<void> => {
		if (operationInFlight.current !== null) return;
		const token = Symbol();
		operationInFlight.current = token;
		track("appearance_colors_restore_start", { field });
		try {
			const restored = await restoreSettings([field]);
			if (!isCurrentOperation(token)) return;
			if (field === "job_colors") setJobPins(restored.job_colors);
			else setRolePins(restored.role_colors);
			track("appearance_colors_restore_success", { field });
			showSnackbar(t("appearance.category.restore.success"), "success");
		} catch (error) {
			trackError("appearance_colors_restore_error", { field, error_message: error instanceof Error ? error.message : String(error) });
			if (isCurrentOperation(token)) showSnackbar(t("appearance.category.restore.failed"), "error");
		} finally {
			if (operationInFlight.current === token) operationInFlight.current = null;
		}
	};

	const handleSelectPreset = (value: string) => {
		setColor("primary", value);
		track("appearance_color_changed", { role: "primary", source: "preset", color: value });
	};

	const handleReset = () => {
		resetAll();
		track("appearance_reset");
	};

	return (
		<>
			<SystemDialog
				open={open}
				onClose={requestClose}
				maxWidth="sm"
				fullWidth
				dataTestId="appearance-dialog"
				title={
					<Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
						<Palette fontSize="small" />
						<span>{t("appearance.title")}</span>
					</Stack>
				}
				contentSx={{ borderTop: 1, borderBottom: 1, borderColor: "divider" }}
				body={
					<>
						<Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
							{t("appearance.description")}
						</Typography>

						{isSupport && (
							<>
								<Typography variant="subtitle2" sx={{ mb: 1.5 }}>
									{t("appearance.logo")}
								</Typography>
								<Box sx={{ mb: 3 }}>
									<LogoPicker
										value={logo}
										onChange={(id) => {
											setLogo(id);
											track("appearance_logo_changed", { logo: id });
										}}
									/>
								</Box>
							</>
						)}

						<Typography variant="subtitle2" sx={{ mb: 1 }}>
							{t("appearance.presets")}
						</Typography>
						<Stack direction="row" spacing={1.25} useFlexGap sx={{ flexWrap: "wrap", mb: 3 }}>
							{THEME_PRESETS.map((preset) => {
								const selected = preset.value.toLowerCase() === colors.primary.toLowerCase();
								return (
									<Tooltip key={preset.id} title={t(preset.labelKey)}>
										<IconButton
											data-testid={`appearance-preset-${preset.id}`}
											aria-label={t(preset.labelKey)}
											onClick={() => handleSelectPreset(preset.value)}
											sx={{
												width: SWATCH_SIZE,
												height: SWATCH_SIZE,
												backgroundColor: preset.value,
												border: selected ? "3px solid" : "1px solid",
												borderColor: selected ? "text.primary" : "divider",
												transition: "transform 120ms",
												"&:hover": { backgroundColor: preset.value, transform: "scale(1.08)" },
											}}
										>
											{selected && <Check sx={{ color: pickContrastText(preset.value), fontSize: 20 }} />}
										</IconButton>
									</Tooltip>
								);
							})}
						</Stack>

						<Typography variant="subtitle2" sx={{ mb: 1.5 }}>
							{t("appearance.colors")}
						</Typography>
						<Stack spacing={1.5} sx={{ mb: 3 }}>
							{COLOR_ROLES.map((role) => (
								<ColorRoleField
									key={role}
									role={role}
									label={t(`appearance.role.${role}`)}
									value={colors[role]}
									invalidHelper={t("appearance.invalid.hex")}
									onChange={(hex) => setColor(role, hex)}
									onCommit={(hex) => track("appearance_color_changed", { role, source: "hex", color: hex })}
								/>
							))}
						</Stack>

						<Typography variant="subtitle2" sx={{ mb: 1.5 }}>
							{t("appearance.fonts")}
						</Typography>
						<Stack spacing={1.5}>
							{FONT_ROLES.map((role) => (
								<FontRoleField
									key={role}
									role={role}
									label={t(`appearance.role.${role}`)}
									value={fonts[role]}
									onChange={(id) => {
										setFont(role, id);
										track("appearance_font_changed", { role, font: id });
									}}
								/>
							))}
						</Stack>
						<Box sx={{ mt: 3, borderTop: 1, borderColor: "divider", pt: 2 }}>
							<Typography variant="subtitle1" sx={{ mb: 0.5 }}>
								{t(
									jobManagementEnabled && enabledJobIds !== undefined && enabledJobIds.length > 0
										? "appearance.category.title"
										: "appearance.category.roles.title",
									{
										department: chosenDepartment.name,
									},
								)}
							</Typography>
							<Typography variant="body2" sx={{ color: "text.secondary", mb: 1.5 }}>
								{t("appearance.category.description")}
							</Typography>
							{(settings.isLoading || (settings.data && initializedForContext && !jobCatalogReady && !jobCatalogUnavailable)) && (
								<Typography data-testid="appearance-colors-loading" variant="body2">
									{t("appearance.category.loading")}
								</Typography>
							)}
							{settings.isError && (
								<Stack spacing={1}>
									<Typography color="error" data-testid="appearance-colors-error">
										{t("appearance.category.load.failed")}
									</Typography>
									<SystemButton buttonVariant="secondary" data-testid="appearance-colors-retry" onClick={() => settings.refetch()}>
										{t("appearance.category.retry")}
									</SystemButton>
								</Stack>
							)}
							{settings.data && initializedForContext && jobCatalogUnavailable && (
								<Stack spacing={1}>
									<Typography color="error" data-testid="appearance-jobs-error">
										{t("appearance.category.jobs.load.failed")}
									</Typography>
									{enabledJobIds !== undefined && (
										<SystemButton buttonVariant="secondary" data-testid="appearance-jobs-retry" onClick={() => allJobs.refetch()}>
											{t("appearance.category.retry")}
										</SystemButton>
									)}
								</Stack>
							)}
							{settings.data && initializedForContext && (
								<>
									{jobCatalogReady && jobEntries.length === 0 && roleEntries.length === 0 && (
										<EmptyState
											icon={<Palette />}
											title={t(jobManagementEnabled ? "appearance.category.empty.jobs.roles" : "appearance.category.empty.roles")}
											hint={t(jobManagementEnabled ? "appearance.category.empty.jobs.roles.hint" : "appearance.category.empty.roles.hint")}
											dataTestId="appearance-colors-empty"
										/>
									)}
									{jobEntries.length > 0 && (
										<CategoricalColorPinsSection
											entries={jobEntries}
											pins={jobPins}
											resolveColor={(id) => jobColor(id)}
											onChange={(next, id, slot) => {
												setJobPins(next);
												track("job_color_changed", { job_id: id, color_slot: slot, is_automatic: slot === null });
											}}
											onRestore={() => restoreColors("job_colors")}
											restoring={isRestoring}
											dataTestId="job-color"
											copy={{
												title: t("settings.job.colors.title"),
												description: t("settings.job.colors.description"),
												defaultChoice: t("settings.job.colors.default"),
												defaultHint: t("settings.job.colors.default.hint"),
												personalHint: t("settings.job.colors.personal.hint"),
												colorNames,
												pickerLabel: (label) => t("settings.job.colors.picker.label", { job: label }),
												duplicate: (label) => t("settings.job.colors.duplicate", { job: label }),
												restore: t("settings.job.colors.restore"),
											}}
										/>
									)}
									{roleEntries.length > 0 && (
										<CategoricalColorPinsSection
											entries={roleEntries}
											pins={rolePins}
											resolveColor={(id) => roleColor(NurseFullRole[id as keyof typeof NurseFullRole])}
											onChange={(next, id, slot) => {
												setRolePins(next);
												track("role_color_changed", { role: id, color_slot: slot, is_automatic: slot === null });
											}}
											onRestore={() => restoreColors("role_colors")}
											restoring={isRestoring}
											dataTestId="role-color"
											copy={{
												title: t("settings.role.colors.title"),
												description: t("settings.role.colors.description"),
												defaultChoice: t("settings.job.colors.default"),
												defaultHint: t("settings.role.colors.default.hint"),
												personalHint: t("settings.role.colors.personal.hint"),
												colorNames,
												pickerLabel: (label) => t("settings.role.colors.picker.label", { role: label }),
												duplicate: (label) => t("settings.role.colors.duplicate", { role: label }),
												restore: t("settings.role.colors.restore"),
											}}
										/>
									)}
								</>
							)}
						</Box>
					</>
				}
				actionsSx={{
					justifyContent: "space-between",
					flexWrap: "wrap",
					rowGap: 1,
					flexDirection: { xs: "column", sm: "row" },
					alignItems: { xs: "stretch", sm: "center" },
				}}
				actionsStart={
					<SystemButton buttonVariant="borderless" leadingIcon={<RestartAlt />} onClick={handleReset} disabled={!isCustom} data-testid="appearance-reset">
						{t("appearance.reset")}
					</SystemButton>
				}
				secondaryAction={
					saveAsDefaultEnabled && colorsChanged
						? {
								label: t("appearance.category.save.default"),
								onClick: () => setConfirmDefault(true),
								disabled: isSaving || isRestoring,
							}
						: undefined
				}
				secondaryDataTestId="appearance-save-default"
				confirm={colorsChanged ? t("appearance.category.save.close") : t("appearance.done")}
				confirmButtonVariant="primary"
				confirmDisabled={isSaving || isRestoring}
				confirmLoading={isSaving}
				confirmDataTestId="appearance-done"
				hideCancel
				onConfirm={() => {
					if (colorsChanged) void saveColors(false);
					else requestClose();
				}}
			/>
			<SaveAsDefaultDialog
				open={open && confirmDefault}
				onClose={() => setConfirmDefault(false)}
				onConfirm={() => {
					setConfirmDefault(false);
					return saveColors(true);
				}}
				extraNotice={defaultNotice}
				dataTestId="appearance-save-default-confirm"
			/>
			<SystemDialog
				open={open && confirmDiscard}
				onClose={() => setConfirmDiscard(false)}
				title={t("appearance.category.discard.title")}
				body={t("appearance.category.discard.body")}
				confirm={t("appearance.category.discard")}
				cancel={t("cancel")}
				onConfirm={() => {
					setConfirmDiscard(false);
					onClose();
				}}
				dataTestId="appearance-discard-confirm"
			/>
		</>
	);
};
